import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ApiError,
  bookKey,
  can,
  getBook,
  getChapter,
  getManifest,
  getRenderEstimate,
  latestRender,
  listChapterImages,
  renameChapter,
} from "../api";
import { useT } from "../i18n";
import { primaryOrg, useSession } from "../session";
import BackLink from "../components/BackLink";
import EditableTitle from "../components/EditableTitle";
import { PencilIcon } from "../components/icons";
import ChapterPlayer from "../player/ChapterPlayer";
import RecordingBadge from "../player/RecordingBadge";
import ReadingText from "../player/ReadingText";
import { useSpeakers } from "../player/speakers";
import ChapterWorkbench, { type EditStart } from "../studio/ChapterWorkbench";
import NarrationPanel from "../studio/NarrationPanel";
import { passageLine } from "../studio/passage";
import { lineAtTop, showLine, type ViewAnchor } from "../studio/viewport";
import FixList from "./FixList";

/**
 * ChapterPage shows the text, and — once there is audio — the player.
 *
 * Rendering is a spend, so the estimate is on screen before the button is.
 */
export default function ChapterPage() {
  const { chapterID = "" } = useParams();
  const { me } = useSession();
  const org = primaryOrg(me);
  const client = useQueryClient();
  const t = useT();
  const canWrite = can(org, "chapter.write");
  const canRewrite = can(org, "chapter.draft");

  /**
   * Editing replaces the page's machinery with the chapter, text and pictures
   * both, changeable in place (ADR-0051). Opened from the button that is
   * always on screen, from the nav, or from a long press on a line — which
   * arrives with that line. The author's place comes with them either way.
   */
  const [editing, setEditing] = useState<EditStart | null>(null);
  const returning = useRef<ViewAnchor | null>(null);

  // Whether to play audio recorded from an earlier version of the text, with
  // that version's transcript, instead of showing the text as it is now.
  const [playEarlier, setPlayEarlier] = useState(false);

  // The working copy (ADR-0024): the studio edits and narrates what is being
  // written, not what readers were last given.
  const chapter = useQuery({
    queryKey: ["chapter", chapterID, "draft"],
    queryFn: () => getChapter(chapterID, { draft: true }),
  });

  const bookID = chapter.data?.book_id;

  const manifest = useQuery({
    queryKey: ["manifest", chapterID, "draft"],
    queryFn: () => getManifest(chapterID, { draft: true }),
    // A 404 means "not narrated yet", which is a state, not a failure.
    retry: false,
    // The URLs inside expire; never serve a cached manifest with dead links.
    gcTime: 0,
    staleTime: 0,
  });

  // The chapter's pictures, shown in place in the reading view and the player
  // as well as in the pictures mode — so the author is never deciding from a
  // preview that differs from the page.
  // The book, for its link. The same key the studio's book page uses, so
  // arriving from it costs nothing.
  const book = useQuery({
    queryKey: bookKey(bookID ?? "", { draft: true }),
    queryFn: () => getBook(bookID!, { draft: true }),
    enabled: !!bookID,
  });
  const images = useQuery({
    queryKey: ["chapter-images", chapterID, "draft"],
    queryFn: () => listChapterImages(chapterID, { draft: true }),
    retry: false,
  });

  const estimate = useQuery({
    queryKey: ["estimate", chapterID],
    queryFn: () => getRenderEstimate(chapterID),
    enabled: can(org, "narration.render"),
    retry: false,
  });

  const retitle = useMutation({
    mutationFn: (title: string) => renameChapter(chapterID, title),
    onSuccess: () => client.invalidateQueries({ queryKey: ["chapter", chapterID] }),
  });

  const notNarrated =
    manifest.isError && manifest.error instanceof ApiError && manifest.error.status === 404;

  /**
   * The audio is of an earlier text. The player shows the audio's own
   * transcript — it has to, the word timings belong to it — so after an edit it
   * showed the words that were recorded, not the words the chapter now has, and
   * a saved edit looked lost. The studio shows the current text instead, and
   * keeps the earlier audio one tap away.
   *
   * Only when both revisions are known: an unknown one is not evidence of a
   * difference. And only when narrating would actually record something
   * different — a chapter edited to say what its correction already said names
   * an earlier revision while every part still carries over unchanged, so the
   * transcript *is* the current text. Hiding the player and showing the words
   * as plain text there is a whole page rearranged around a uuid.
   */
  const audioOutdated =
    !!manifest.data?.revision_id &&
    !!chapter.data?.revision_id &&
    manifest.data.revision_id !== chapter.data.revision_id &&
    estimate.data?.needs_render !== false;

  const startEditing = (line?: string, rewrite = false, noSelect = false) => {
    setEditing({ line: line ?? null, rewrite, anchor: lineAtTop(), noSelect });
  };

  // Back in the reading view, with the words that were at the top of the
  // editor at the top of the page.
  useLayoutEffect(() => {
    const anchor = returning.current;
    if (editing || !anchor) return;
    returning.current = null;
    showLine(anchor);
  }, [editing]);

  // A linked chapter's **words are the author's** and its **pictures are the
  // source's** (ADR-0098 §1): the text may be fixed by hand, and every picture
  // write is still refused. So editing and renaming come back, and the
  // workbench gates the pictures itself — one change in two places, because the
  // picture controls live inside the mode these entries open.
  //
  // The chapter, its text and its pictures all stay on the screen either way:
  // leaving the controls out is not leaving the screen out, which is the
  // mistake the Characters and Cover rows made first (ADR-0093's *Since*).
  const derived = !!book.data?.linked_source_id;
  const canEdit = canWrite && !!chapter.data;

  /**
   * Show in text (ADR-0120): `?passage=<part id>` opens the editor on that
   * passage — the words the check did not hear when they are known, else its
   * first sentence. A part never changes, so its id is a stable anchor; the
   * editor finds the words by their text, and says so when a correction has
   * changed them. The parameter is spent on arrival, so Back does not reopen
   * the editor.
   */
  const [params, setParams] = useSearchParams();
  const passage = params.get("passage");
  const latest = useQuery({
    queryKey: ["latest-render", chapterID],
    queryFn: () => latestRender(chapterID),
    retry: false,
    staleTime: 0,
    enabled: !!passage,
  });
  useEffect(() => {
    if (!passage || !chapter.data || latest.isPending) return;
    setParams(
      (prev) => {
        prev.delete("passage");
        return prev;
      },
      { replace: true },
    );
    const part = latest.data?.parts?.find((p) => p.id === passage);
    if (canEdit && part?.text) {
      const at = passageLine(part.text, part.failure, part.unsaid_start, part.unsaid_end);
      startEditing(at.line, false, !at.select);
    }
    // Once per passage asked for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passage, chapter.data, latest.isPending]);
  const playerShown = !!manifest.data && (!audioOutdated || playEarlier);
  // Who says each quote, placed by the server in whichever text is on screen:
  // the audio's revision under the player, the current one otherwise
  // (ADR-0125). Correcting takes book.augment, so it is shown to whoever may.
  const speakers = useSpeakers(
    chapterID,
    chapter.data?.book_id,
    playerShown ? manifest.data?.revision_id : (chapter.data?.revision_id ?? undefined),
    can(org, "book.augment") &&
      !!chapter.data &&
      (playerShown ? !!manifest.data?.revision_id : true),
  );

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <nav className="mb-6 flex items-center justify-between gap-3 text-sm">
        {/* Back to the studio's book page, where this chapter was opened from.
            It pointed at the reader's book page, whose own back link is the
            library — so leaving a chapter in the studio left the studio. And
            only once the chapter has loaded: before that there is no book to go
            back to, and a guessed destination took a quick tap somewhere else. */}
        {chapter.data ? (
          <BackLink to={`/studio/books/${chapter.data.book_id}`}>{t.narration.backToBook}</BackLink>
        ) : (
          <span />
        )}
        {/* The same chapter without the machinery, so a creator can see what a
            reader sees rather than guessing at it. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* The corrections are a button that opens a sheet, not a panel on
              the page: they are consulted now and then, and the text is what
              this page is for. */}
          {!editing && <FixList chapterID={chapterID} />}
          {canEdit && !editing && (
            <button
              type="button"
              data-testid="edit-text"
              onClick={() => startEditing()}
              className="rounded-control border border-line px-2.5 py-1 text-xs text-ink-soft transition-colors hover:bg-sunk"
            >
              {t.edit.editText}
            </button>
          )}
          <Link
            to={`/read/${chapterID}`}
            data-testid="open-reader"
            className="rounded-control border border-line px-2.5 py-1 text-xs text-ink-soft transition-colors hover:bg-sunk"
          >
            {t.narration.read}
          </Link>
        </div>
      </nav>

      <header className="mb-6 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <EditableTitle
            value={chapter.data?.title ?? "…"}
            canEdit={can(org, "chapter.write") && !!chapter.data}
            onSave={(title) => retitle.mutateAsync(title)}
            label={t.book.chapterNoun}
            testId="chapter-title"
            className="text-2xl font-semibold tracking-tight"
          />
        </div>
        {/* What the audio in hand was made with, which is not what the
            settings below would make next. Comparing two speech models by ear
            is exactly the moment somebody needs to be told which one they are
            listening to. */}
        {manifest.data?.recording && <RecordingBadge recording={manifest.data.recording} />}
      </header>

      {!editing && can(org, "narration.render") && (
        <NarrationPanel chapterID={chapterID} bookID={bookID} />
      )}

      {!editing && notNarrated && (
        <p data-testid="not-narrated" className="mb-8 text-sm text-muted">
          {t.narration.noAudio}
        </p>
      )}

      {!editing && audioOutdated && (
        <div
          data-testid="audio-outdated"
          className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-control bg-sunk px-3 py-2 text-sm text-ink-soft"
        >
          <span>{playEarlier ? t.narration.playingEarlier : t.narration.audioOutdated}</span>
          <button
            type="button"
            data-testid="toggle-earlier-audio"
            onClick={() => setPlayEarlier((on) => !on)}
            className="rounded-control border border-line px-2.5 py-1 text-xs transition-colors hover:bg-surface"
          >
            {playEarlier ? t.narration.showCurrentText : t.narration.playEarlierAudio}
          </button>
        </div>
      )}

      {!editing && manifest.data && playerShown && (
        <ChapterPlayer
          chapterID={chapterID}
          title={chapter.data?.title}
          manifest={manifest.data}
          images={images.data?.items}
          allowCorrections
          draft
          onEditLine={canWrite ? (line, rewrite) => startEditing(line, rewrite) : undefined}
          allowRewrite={canRewrite}
          speakers={speakers.speakers}
        />
      )}

      {editing && chapter.data && (
        <ChapterWorkbench
          chapterID={chapterID}
          bookID={chapter.data.book_id}
          body={chapter.data.body}
          revisionID={chapter.data.revision_id ?? ""}
          canDescribe={can(org, "chapter.illustrate") && !derived}
          canIllustrate={!derived}
          canRewrite={canRewrite}
          start={editing}
          onDone={(anchor) => {
            returning.current = anchor;
            setEditing(null);
          }}
        />
      )}

      {/* Edit, wherever the author has scrolled to. Above the transport when
          there is one, beside the thumb that is already there. */}
      {canEdit && !editing && (
        <button
          type="button"
          data-testid="edit-fab"
          aria-label={t.edit.editHint}
          onClick={() => startEditing()}
          className={`fixed end-4 z-20 flex items-center gap-1.5 rounded-full bg-sage px-4 py-3 text-sm font-medium text-on-sage shadow-lg transition-colors hover:bg-sage-deep ${
            playerShown
              ? "bottom-[calc(6rem+env(safe-area-inset-bottom))]"
              : "bottom-[max(1rem,env(safe-area-inset-bottom))]"
          }`}
        >
          <PencilIcon className="size-4" />
          {t.edit.editText}
        </button>
      )}
      {/* Room for that button, so it never sits on the last line. */}
      {canEdit && !editing && <div aria-hidden="true" className="h-16" />}

      {!editing && (!manifest.data || (audioOutdated && !playEarlier)) && chapter.data?.body && (
        /* The reader's own component, with nothing to ask, so the studio
             sees exactly the page a reader gets — pictures included. It
             carries the `chapter-text` element itself, so there is no wrapper
             here to duplicate that name. */
        <ReadingText
          chapterID={chapterID}
          bookID={chapter.data.book_id}
          language={book.data?.language ?? ""}
          body={chapter.data.body}
          emphasis={chapter.data.emphasis}
          allowAsking={false}
          images={images.data?.items}
          speakers={speakers.speakers}
        />
      )}
      {!editing && speakers.sheet}

      {/* After editing or narrating, the next question is whether readers have
          it. Answered here, with the way to the publish screen, rather than
          two screens back. */}
      {!editing &&
        chapter.data &&
        can(org, "book.publish") &&
        chapter.data.edition_state &&
        chapter.data.edition_state !== "current" && (
          <p
            data-testid="publish-line"
            data-state={chapter.data.edition_state}
            className="mt-10 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-4 text-xs text-muted"
          >
            <span>
              {chapter.data.edition_state === "outdated"
                ? t.book.publishLineOutdated
                : t.book.publishLineUnpublished}
            </span>
            <Link
              to={`/studio/books/${chapter.data.book_id}/publish`}
              data-testid="publish-line-link"
              className="rounded-control border border-line px-2.5 py-1 text-xs text-ink-soft transition-colors hover:bg-sunk"
            >
              {t.book.publishLineLink}
            </Link>
          </p>
        )}
    </main>
  );
}
